import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import TableTab from "../components/tabs/table-tab";
import { SITES } from "../constants/menu-types";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/SiteColumns';
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from '../models/search/SiteSearch';
import TimeZoneSearch from "../models/search/TimeZoneSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { deleteSite, getSites } from "../services/siteService";
import { getTimeZones } from "../services/timeZoneService";
import { Filter } from "../models/Filter";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";
import { removeSiteAction, updateSitesAction } from "../store/site/actions";
import { IAppState } from "../store/types";

interface Props {
    setBusyDeleting: (isBusyDeleting: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange: (filters: Filter[]) => void
}

const SitesTab: FC<Props> = ({ setBusyDeleting, categories, setCategories, onFilterChange }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.sites);
    const curSiteId = useSelector((state: IAppState) => state.site.site.value);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const itemPath = '/access-control/configuration/site/';
    const dispatch = useDispatch();

    useEffect(() => {
        setLookup([{
            name: LookupTypes.timeZone,
            values: [],
            dataMethod: getTimeZones,
            searchAccessor: TimeZoneSearch.find(item => item.accessor === "name")?.searchOnName
        }]);
    }, [])

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusyDeleting(true);
        return new Promise((resolve, reject) => {
            deleteSite(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        dispatch(removeSiteAction(id));
                        dispatch(updateSitesAction(true));
                        setBusyDeleting(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusyDeleting(false);
                    })
        });
    }

    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = SITES;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail) => void) => {
        setDetails(userLoginId, currentMenuDetail);
    }

    return <TableContextProvider
        key={curSiteId}
        dataMethod={getSites}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        deleteMethod={onDelete}
        useTable
        usePagination
        useFilter
        useSearch
        useSort
        lookup={lookup}
        customDelete={{ fieldName: 'isDefault', comparisonValue: false }}
        customDeleteWithRange={{ fieldName: "id", excludedValues: [curSiteId] }} >
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath} 
            cyName="sites"/>
    </TableContextProvider>;
};

export default SitesTab;
